/**
 * UploadRecordingDialog — the page-level upload dialog on /portal/recordings.
 *
 * Handles ONE or MANY files (Sam 2026-08-12: bulk-uploading numbered files
 * like "001.mp3" must be painless):
 *
 *   - Exactly one file selected → the classic single-file layout: audio
 *     preview + Name + Description fields. Name defaults from the filename
 *     (extension stripped); a user-typed name is never overwritten.
 *   - Two or more files → a queue: one row per file with an editable Name
 *     (each defaulting from its own filename), size, and a remove button.
 *     Files upload sequentially against the existing single-file POST;
 *     a failure marks that row and the queue continues. Summary toast at
 *     the end ("10 uploaded, 2 failed"); failed rows stay for retry.
 *
 * The dialog cannot be closed while an upload run is in flight.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { recordings } from '@/api/recordings';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { RotateCcw, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';

const ACCEPTED_MIMES = [
  'audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/ogg',
  'audio/webm', 'audio/mp4', 'audio/x-m4a',
];
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB — supports up to ~4 h audio

type EntryStatus = 'queued' | 'uploading' | 'done' | 'failed';

interface UploadEntry {
  /** Stable React key (files can share names). */
  key: string;
  file: File;
  name: string;
  /**
   * True once the user typed in the Name field. A typed name survives a
   * "Change file" swap; an untouched default is replaced by the new file's
   * own default — so numbered batches always pick up each file's number.
   */
  nameEdited: boolean;
  status: EntryStatus;
  error?: string;
}

function defaultNameFor(file: File): string {
  return file.name.replace(/\.[^.]+$/, '');
}

function entryValidationError(entry: UploadEntry): string | null {
  if (!ACCEPTED_MIMES.includes(entry.file.type)) return 'Unsupported file type.';
  if (entry.file.size > MAX_BYTES) return 'File exceeds 500 MB limit.';
  if (!entry.name.trim()) return 'Name is required.';
  return null;
}

function friendlyUploadError(raw: string): string {
  if (raw.includes('file_too_large')) return 'File exceeds the 500 MB upload limit.';
  if (raw.includes('unsupported_media_type') || raw.includes('invalid_mime') || raw.includes('unsupported_mime'))
    return 'Unsupported file type. Please upload WAV, MP3, OGG, WEBM, or M4A.';
  if (raw.includes('recording_too_long')) return "Recordings can't be longer than 1 hour.";
  if (raw.includes('over_limit') || raw.includes('limit_exceeded'))
    return 'Storage limit reached. Delete some recordings or upgrade your plan.';
  if (raw.includes('customer_id_required')) return 'Pick a customer before uploading.';
  return raw || 'Upload failed.';
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('animate-spin', className)} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function UploadRecordingDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const keyCounter = useRef(0);
  // Carries a user-typed name across "Change file" in single mode.
  const carryNameRef = useRef<string | null>(null);

  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [description, setDescription] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [batchDone, setBatchDone] = useState(false);
  const { effectiveCustomerId, element: customerPicker } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const single = entries.length === 1 ? entries[0]! : null;

  // Blob URL for the single-file preview — lets the user LISTEN before
  // committing to upload. Memoised per-file; revoked when replaced.
  const previewUrl = useMemo(
    () => (single ? URL.createObjectURL(single.file) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [single?.file],
  );
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  function resetForm() {
    setEntries([]);
    setDescription('');
    setFieldErrors({});
    setCustomerId(null);
    setBatchDone(false);
    setCurrentIndex(null);
    carryNameRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function addFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const carried = carryNameRef.current;
    carryNameRef.current = null;
    setEntries((prev) => [
      ...prev,
      ...arr.map((f, i) => {
        // "Change file" in single mode: a name the user TYPED carries over
        // to the replacement file; a defaulted name is re-derived from the
        // new file so numbered batches stay accurate.
        const useCarry = carried !== null && prev.length === 0 && arr.length === 1 && i === 0;
        return {
          key: `f${++keyCounter.current}`,
          file: f,
          name: useCarry ? carried : defaultNameFor(f),
          nameEdited: useCarry,
          status: 'queued' as EntryStatus,
        };
      }),
    ]);
    setBatchDone(false);
    setFieldErrors((e) => {
      if (!e.file) return e;
      const { file: _dropped, ...rest } = e;
      void _dropped;
      return rest;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeSingleFile() {
    const entry = entries[0];
    if (entry && entry.nameEdited && entry.name.trim()) {
      carryNameRef.current = entry.name;
    }
    setEntries([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFieldErrors({});
  }

  function removeFromQueue(key: string) {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  }

  function updateName(key: string, name: string) {
    setEntries((prev) =>
      prev.map((e) => (e.key === key ? { ...e, name, nameEdited: true } : e)),
    );
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (running) return;
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }

  async function runQueue() {
    setRunning(true);
    setBatchDone(false);
    const list = entries.map((e) => ({ ...e }));

    for (let i = 0; i < list.length; i++) {
      const entry = list[i]!;
      if (entry.status === 'done') continue; // retry pass skips successes

      const vErr = entryValidationError(entry);
      if (vErr) {
        list[i] = { ...entry, status: 'failed', error: vErr };
        setEntries([...list]);
        continue;
      }

      setCurrentIndex(i);
      list[i] = { ...entry, status: 'uploading', error: undefined };
      setEntries([...list]);

      try {
        const fd = new FormData();
        fd.append('file', entry.file);
        fd.append('name', entry.name.trim());
        // Description only applies in single-file mode — there's one field.
        if (list.length === 1 && description.trim()) fd.append('description', description.trim());
        // Backend requires customer_id for super_admin + reseller_owner
        // uploads. Always send when we have one (see CLAUDE.md — never gate
        // the body on picker visibility); the server ignores it for
        // office_manager / standard_user and reads their tenant scope.
        if (effectiveCustomerId) fd.append('customer_id', effectiveCustomerId);

        await recordings.upload(fd);
        // Refresh the list behind the dialog after each success so long
        // batches show up progressively.
        qc.invalidateQueries({ queryKey: ['recordings'] });
        list[i] = { ...entry, status: 'done', error: undefined };
      } catch (err) {
        const raw = err instanceof Error ? err.message : 'Upload failed.';
        list[i] = { ...entry, status: 'failed', error: friendlyUploadError(raw) };
        // Failures don't stop the queue — keep going.
      }
      setEntries([...list]);
    }

    setCurrentIndex(null);
    setRunning(false);

    const ok = list.filter((e) => e.status === 'done').length;
    const failed = list.filter((e) => e.status === 'failed').length;
    if (failed === 0) {
      toast({
        title: ok === 1 ? 'Recording uploaded' : `${ok} recordings uploaded`,
      });
      resetForm();
      onOpenChange(false);
    } else if (list.length > 1) {
      toast({
        title: `${ok} uploaded, ${failed} failed`,
        description: 'Failed files stay listed — fix them and press Retry.',
        variant: 'destructive',
      });
      setBatchDone(true);
    }
    // Single-file failure: the inline error under the file card is the
    // signal (classic behavior) — no toast needed.
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Defensive (rule 18): never let this submit bubble to a hosting form.
    e.stopPropagation();
    if (running) return;

    const errs: Record<string, string> = {};
    if (effectiveCustomerId === null) errs.customer = 'Customer is required.';
    if (entries.length === 0) {
      errs.file = 'Please select an audio file.';
    } else if (entries.length === 1) {
      // Single mode keeps the classic inline field errors.
      const entry = entries[0]!;
      if (!ACCEPTED_MIMES.includes(entry.file.type)) errs.file = 'Unsupported file type.';
      else if (entry.file.size > MAX_BYTES) errs.file = 'File exceeds 500 MB limit.';
      if (!entry.name.trim()) errs.name = 'Name is required.';
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    void runQueue();
  }

  const pendingCount = entries.filter((e) => e.status !== 'done').length;
  const doneCount = entries.filter((e) => e.status === 'done').length;
  const failedCount = entries.filter((e) => e.status === 'failed').length;

  const submitDisabled =
    running
    || entries.length === 0
    || (entries.length === 1 && !entries[0]!.name.trim());

  const submitLabel = running
    ? (entries.length > 1
        ? `Uploading ${currentIndex !== null ? currentIndex + 1 : '…'} of ${entries.length}…`
        : 'Uploading…')
    : batchDone && failedCount > 0
      ? `Retry ${failedCount} failed`
      : entries.length > 1
        ? `Upload ${pendingCount} files`
        : 'Upload';

  const dropZone = (compact: boolean) => (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !running && fileInputRef.current?.click()}
      role="button"
      tabIndex={0}
      aria-label="Drop audio files here or click to browse"
      onKeyDown={(e) => {
        if (!running && (e.key === 'Enter' || e.key === ' ')) fileInputRef.current?.click();
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 transition-colors select-none',
        compact ? 'py-4' : 'py-8',
        running ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        dragOver
          ? 'border-blue-500 bg-blue-50'
          : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100',
      )}
    >
      {/* Microphone-wave icon */}
      <svg viewBox="0 0 40 40" className={cn('text-slate-400', compact ? 'h-7 w-7' : 'h-10 w-10')} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="14" y="4" width="12" height="20" rx="6" />
        <path d="M8 20c0 6.627 5.373 12 12 12s12-5.373 12-12" />
        <line x1="20" y1="32" x2="20" y2="38" />
        <line x1="14" y1="38" x2="26" y2="38" />
      </svg>
      <div className="text-center">
        <p className="text-sm font-medium text-slate-600">
          {entries.length > 0 ? 'Add more files' : 'Drop audio here or click to browse'}
        </p>
        {!compact && (
          <p className="text-xs text-slate-400 mt-0.5">
            Select several files to batch-upload — each gets its own name.
          </p>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!running) { onOpenChange(v); if (!v) resetForm(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload recording</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5">
            {/* Customer scope indicator.
                - super_admin / reseller_owner WITH an entered customer: show
                  the chip as a read-only reassurance that uploads land in the
                  right place.
                - super_admin / reseller_owner WITHOUT an entered customer:
                  fall back to the legacy inline picker.
                - office_manager / standard_user: nothing renders; tenant scope
                  provides the customer_id implicitly. */}
            <CustomerScopeChip />
            {customerPicker}
            {fieldErrors.customer && (
              <p role="alert" className="text-xs text-red-500 -mt-3">{fieldErrors.customer}</p>
            )}

            {single ? (
              /* Single-file state — the user can LISTEN to the file before
                 pressing Upload, and REMOVE it to swap in a different file.
                 Mirrors the RecordInBrowserDialog "stopped" state. */
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-700">
                      {single.file.name}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {(single.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={removeSingleFile}
                    disabled={running}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Change file
                  </Button>
                </div>
                {previewUrl && <AudioPlayer src={previewUrl} />}
                {single.status === 'failed' && single.error && (
                  <p role="alert" className="text-xs text-red-500">{single.error}</p>
                )}
              </div>
            ) : (
              dropZone(entries.length > 1)
            )}
            {/* File input outside the conditional so its ref is stable
                across toggles and keeps working after Change file. */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MIMES.join(',')}
              multiple
              className="sr-only"
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); }}
              aria-hidden="true"
            />
            {fieldErrors.file && (
              <p role="alert" className="text-xs text-red-500 -mt-3">{fieldErrors.file}</p>
            )}

            {single ? (
              <>
                {/* Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="rec-name">Name <span aria-hidden>*</span></Label>
                  <Input
                    id="rec-name"
                    value={single.name}
                    onChange={(e) => updateName(single.key, e.target.value)}
                    placeholder="e.g. Main menu greeting"
                    autoComplete="off"
                  />
                  {fieldErrors.name && (
                    <p role="alert" className="text-xs text-red-500">{fieldErrors.name}</p>
                  )}
                </div>

                {/* Description (optional) */}
                <div className="space-y-1.5">
                  <Label htmlFor="rec-desc">
                    Description <span className="text-xs text-slate-400">(optional)</span>
                  </Label>
                  <Input
                    id="rec-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short note about this recording"
                  />
                </div>
              </>
            ) : entries.length > 1 ? (
              /* Multi-file queue: one row per file with its own editable
                 name (defaulted from the filename — "001.mp3" → "001"). */
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {running && currentIndex !== null && (
                  <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
                    Uploading file {currentIndex + 1} of {entries.length}…
                  </div>
                )}
                {batchDone && failedCount > 0 && (
                  <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
                    {doneCount} uploaded, {failedCount} failed. Fix the rows below and press Retry.
                  </div>
                )}
                <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {entries.map((entry) => (
                    <li
                      key={entry.key}
                      className={cn(
                        'flex items-start gap-2 px-3 py-2 text-xs',
                        entry.status === 'done' && 'bg-green-50/60',
                        entry.status === 'failed' && 'bg-red-50/60',
                        entry.status === 'uploading' && 'bg-blue-50/60',
                      )}
                    >
                      {/* Status icon */}
                      <div className="mt-0.5 shrink-0">
                        {entry.status === 'done' && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                        {entry.status === 'failed' && <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
                        {entry.status === 'uploading' && <SpinnerIcon className="h-3.5 w-3.5 text-blue-500" />}
                        {entry.status === 'queued' && (
                          <div className="h-3.5 w-3.5 rounded-full border border-slate-300" />
                        )}
                      </div>

                      {/* File info + editable name */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <p className="truncate text-slate-500">{entry.file.name}</p>
                          <p className="shrink-0 text-[10px] text-slate-400">
                            {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        {entry.status !== 'done' && (
                          <input
                            value={entry.name}
                            onChange={(e) => updateName(entry.key, e.target.value)}
                            disabled={running}
                            placeholder="Recording name"
                            className={cn(
                              'mt-1 w-full rounded border px-1.5 py-0.5 text-[11px] focus:outline-none',
                              'border-slate-200 focus:border-slate-400',
                              running && 'opacity-50 cursor-not-allowed',
                            )}
                            aria-label={`Name for ${entry.file.name}`}
                          />
                        )}
                        {entry.status === 'failed' && entry.error && (
                          <p className="mt-0.5 text-[10px] text-red-600">{entry.error}</p>
                        )}
                      </div>

                      {/* Remove row (not while running, not after success) */}
                      {!running && entry.status !== 'done' && (
                        <button
                          type="button"
                          onClick={() => removeFromQueue(entry.key)}
                          className="mt-0.5 shrink-0 rounded p-0.5 text-slate-300 hover:text-slate-500 hover:bg-slate-100"
                          aria-label={`Remove ${entry.file.name}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={running}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              // Disable when the form is incomplete so the user can SEE
              // they need to fix something before submission can fire.
              // The submit handler also re-validates as a fallback in case
              // something bypasses the disabled state (e.g. keyboard
              // form-submit before a file is set).
              disabled={submitDisabled}
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
